<template>
  <AdminLayout>
    <template #content>
      <div class="d-flex justify-content-between align-items-center mb-3">
        <div>
          <h1 class="h4 mb-1">
            Détail du retour fournisseur
            <span v-if="supplierReturn" class="text-muted">#{{ supplierReturn.reference }}</span>
          </h1>
          <div v-if="supplierReturn" class="small text-muted">
            Créé le {{ $format.datetime(supplierReturn.created_at) }}
          </div>
        </div>

        <div class="d-flex gap-2">
          <button class="btn btn-outline-secondary btn-sm btn-icon-label" title="Retour à la liste" @click="$router.push({ name: 'SupplierReturns' })">
            <i class="fa fa-arrow-left"></i>
            <span class="btn-label ms-1">Retour à la liste</span>
          </button>

          <button
            type="button"
            class="btn btn-outline-primary btn-sm btn-icon-label"
            :disabled="!supplierReturn || supplierReturn.status !== 'draft'"
            title="Modifier"
            @click="$router.push({ name: 'SupplierReturnEdit', params: { id: supplierReturn.id } })"
          >
            <i class="fa fa-pen"></i>
            <span class="btn-label ms-1">Modifier</span>
          </button>

          <button
            type="button"
            class="btn btn-outline-success btn-sm btn-icon-label"
            :disabled="!supplierReturn || supplierReturn.status !== 'draft'"
            title="Valider"
            @click="validateSupplierReturn"
          >
            <i class="fa fa-check"></i>
            <span class="btn-label ms-1">Valider</span>
          </button>

          <AppLoadingButton
            type="button"
            class="btn btn-outline-danger btn-sm btn-icon-label"
            :loading="isDeleting"
            :disabled="!supplierReturn || supplierReturn.status !== 'draft'"
            loading-label="Suppression..."
            title="Supprimer"
            @click="deleteSupplierReturn"
          >
            <i class="fa fa-trash"></i>
            <span class="btn-label ms-1">Supprimer</span>
          </AppLoadingButton>
        </div>
      </div>

      <div v-if="successMessage" class="alert alert-success mb-3 py-2">
        {{ successMessage }}
      </div>

      <div v-if="isLoading" class="text-center my-5">
        <i class="fa fa-spinner fa-spin fa-2x mb-2"></i>
        <div class="small text-muted">Chargement du retour fournisseur...</div>
      </div>

      <div v-else-if="errorMessage" class="alert alert-danger">
        {{ errorMessage }}
      </div>

      <div v-else-if="!supplierReturn" class="alert alert-warning">
        Retour fournisseur introuvable.
      </div>

      <div v-else class="row g-3 align-items-start">
        <div class="col-lg-4">
          <div class="card border-0 shadow-sm">
            <div class="card-header bg-white border-0 pb-1">
              <h6 class="mb-0">Informations générales</h6>
            </div>
            <div class="card-body pt-2 pb-3">
              <div class="mb-2 d-flex justify-content-between align-items-center">
                <span class="small text-muted">Référence</span>
                <span class="fw-semibold">{{ supplierReturn.reference }}</span>
              </div>

              <div class="mb-2">
                <div class="small text-muted">Fournisseur</div>
                <div class="fw-semibold">{{ supplierReturn.supplier_name }}</div>
              </div>

              <div class="mb-2">
                <div class="small text-muted">Commande d'origine</div>
                <router-link
                  v-if="supplierReturn.purchase_id"
                  :to="{ name: 'PurchaseShow', params: { id: supplierReturn.purchase_id } }"
                  class="fw-semibold"
                >
                  {{ supplierReturn.purchase?.reference || `Commande #${supplierReturn.purchase_id}` }}
                </router-link>
                <span v-else>-</span>
              </div>

              <div class="mb-2 d-flex justify-content-between">
                <span class="small text-muted">Date du retour</span>
                <span class="fw-semibold">{{ $format.date(supplierReturn.return_date) }}</span>
              </div>

              <div class="mb-2 d-flex justify-content-between align-items-center">
                <span class="small text-muted">Statut</span>
                <span class="badge" :class="statusClass(supplierReturn.status)">
                  {{ statusLabel(supplierReturn.status) }}
                </span>
              </div>

              <div class="mb-2 d-flex justify-content-between">
                <span class="small text-muted">Montant retourné</span>
                <span class="fw-bold">{{ $format.currency(supplierReturn.total_amount || 0) }}</span>
              </div>

              <div v-if="supplierReturn.validated_at" class="mb-2">
                <div class="small text-muted">Validé le</div>
                <div class="fw-semibold">
                  {{ $format.datetime(supplierReturn.validated_at) }}
                  <span v-if="supplierReturn.validator?.name">
                    par {{ supplierReturn.validator.name }}
                  </span>
                </div>
              </div>

              <div v-if="supplierReturn.comment" class="mt-2">
                <div class="small text-muted">Commentaire</div>
                <div>{{ supplierReturn.comment }}</div>
              </div>
            </div>
          </div>
        </div>

        <div class="col-lg-8">
          <div class="d-flex justify-content-between align-items-center mb-2">
            <h6 class="mb-0">Produits retournés</h6>
            <small class="text-muted" v-if="supplierReturn.items?.length">
              {{ supplierReturn.items.length }} ligne(s)
            </small>
          </div>

          <BootstrapDataTable
            :rows="supplierReturn.items || []"
            :columns="lineColumns"
            empty-text="Aucune ligne de retour."
            @refresh="fetchSupplierReturn"
          >
            <template #cell-product_name="{ row }">
              <div class="fw-semibold">{{ row.product_name }}</div>
              <small class="text-muted">{{ row.product_sku || '-' }}</small>
            </template>

            <template #cell-unit_price="{ row }">
              {{ $format.currency(row.unit_price || 0) }}
            </template>

            <template #cell-lot_number="{ row }">
              {{ row.lot_number || '-' }}
            </template>

            <template #cell-expiry_date="{ row }">
              {{ row.expiry_date ? $format.date(row.expiry_date) : '-' }}
            </template>

            <template #cell-line_total="{ row }">
              {{ $format.currency(row.line_total || 0) }}
            </template>
          </BootstrapDataTable>
        </div>
      </div>

    </template>
  </AdminLayout>
</template>

<script>
export default {
  name: 'SupplierReturnShowView',
  data() {
    return {
      supplierReturn: null,
      isLoading: false,
      errorMessage: '',
      successMessage: '',
      isDeleting: false,
      lineColumns: [
        { key: 'product_name', label: 'Produit' },
        { key: 'lot_number', label: 'Lot' },
        { key: 'expiry_date', label: 'Expiration' },
        { key: 'quantity', label: 'Qté', class: 'text-center' },
        { key: 'unit_price', label: 'PU', class: 'text-end' },
        { key: 'line_total', label: 'Total ligne', class: 'text-end' },
      ],
    };
  },
  mounted() {
    this.fetchSupplierReturn();
  },
  methods: {
    async fetchSupplierReturn() {
      this.isLoading = true;
      this.errorMessage = '';
      this.successMessage = '';
      this.supplierReturn = null;

      try {
        const res = await this.$api.get(`/supplier-returns/${this.$route.params.id}`);
        this.supplierReturn = res.data.data;
      } catch (error) {
        this.errorMessage = 'Impossible de charger ce retour fournisseur.';
      } finally {
        this.isLoading = false;
      }
    },

    statusLabel(status) {
      return status === 'validated' ? 'Validé' : 'Brouillon';
    },

    statusClass(status) {
      return status === 'validated' ? 'bg-success' : 'bg-secondary';
    },

    async validateSupplierReturn() {
      if (!this.supplierReturn || this.supplierReturn.status !== 'draft') return;
      const ok = await this.$confirmAction(
        `Valider le retour "${this.supplierReturn.reference}" ? Les quantités seront retirées du stock.`
      );
      if (!ok) return;

      this.errorMessage = '';
      this.successMessage = '';

      try {
        await this.$api.post(`/supplier-returns/${this.supplierReturn.id}/validate`);
        this.successMessage = 'Retour fournisseur validé avec succès.';
        await this.fetchSupplierReturn();
      } catch (error) {
        this.errorMessage =
          error.response?.data?.errors
            ? Object.values(error.response.data.errors).flat().join(' ')
            : 'Une erreur est survenue lors de la validation du retour fournisseur.';
      }
    },

    async deleteSupplierReturn() {
      if (!this.supplierReturn || this.supplierReturn.status !== 'draft') return;
      if (this.isDeleting) return;

      this.isDeleting = true;

      const ok = await this.$confirmAction(`Supprimer le retour "${this.supplierReturn.reference}" ?`);
      if (!ok) {
        this.isDeleting = false;
        return;
      }

      this.errorMessage = '';
      this.successMessage = '';

      try {
        await this.$api.delete(`/supplier-returns/${this.supplierReturn.id}`);
        this.$router.push({ name: 'SupplierReturns' });
      } catch (error) {
        this.errorMessage =
          error.response?.data?.errors
            ? Object.values(error.response.data.errors).flat().join(' ')
            : 'Une erreur est survenue lors de la suppression du retour fournisseur.';
      } finally {
        this.isDeleting = false;
      }
    },
  },
};
</script>

<style scoped>
.card {
  border-radius: 0.75rem;
}
</style>
